<template xmlns:v-bind="http://www.w3.org/1999/xhtml" xmlns:v-el="http://www.w3.org/1999/xhtml">
  <div
    class="radius-distance-control"
    :class="{ horizontal: useHorizontalLayout }">
    <div class="text-and-unit">
      <icon-radius />
      <input
        v-model="radiusDistanceInternal"
        type="number"
        class="form-control" />
      <tabs-slider
        :selected-tab-i-d="radiusUnitInternal"
        :tabs="distanceUnitTabs"
        width="40rem"
        @selected-tab-changed="
          (tab) => {
            radiusUnitInternal = tab.id;
            changeRadiusUnit(tab.id);
          }
        " />
    </div>
    <div class="slider">
      <vue-slider
        v-model="radiusDistanceInternal"
        :min="radiusDistanceForUnitMin"
        :max="radiusDistanceForUnitMax"
        :piecewise="false"
        :piecewise-label="false"
        :process-style="{ background: 'transparent' }"
        :height="8"
        @drag-start="$emit('dragStart')"
        @drag-end="$emit('dragEnd')" />
    </div>
  </div>
</template>

<script type="text/babel">
  // import { mapState, mapGetters, mapMutations, mapActions } from 'vuex';
  import 'vue-slider-component/theme/antd.css';

  import VueSlider from 'vue-slider-component';

  import TabsSlider from '@/common/components/controls/TabsSlider.vue';
  import DistanceUnit from '@/common/enums/DistanceUnit.enum';
  import { IconRadius } from '@/common/icons/monochrome';
  import UnitConverter from '@/utils/UnitConverter';
  import i18n from '@/vueI18n';

  export default {
    name: 'RadiusDistance',
    components: {
      TabsSlider,
      IconRadius,
      VueSlider,
    },
    props: {
      radiusDistanceInMeters: { type: Number, default: 25 },
      radiusInUnit: { type: Number, default: 25 },
      radiusUnit: { type: String, default: DistanceUnit.METER },
      radiusDistanceMin: { type: Number, default: 1 },
      radiusDistanceMax: { type: Number, default: 10000 },
      useHorizontalLayout: { type: Boolean, default: false },
    },
    data() {
      return {
        preventChangeTrigger: false,
        radiusDistanceInternal: 25,
        radiusUnitInternal: DistanceUnit.METER,
        radiusDistanceForUnitMin: 1,
        radiusDistanceForUnitMax: 5000,
        radiusDistanceMinInternal: 1,
        radiusDistanceMaxInternal: 5000,
        triggerChangeTimer: null,
        triggerTriggerChangeTimer: null,
      };
    },
    computed: {
      distanceUnitTabs() {
        return [
          {
            id: DistanceUnit.METER,
            name: i18n.global.t('options.distanceUnits.meters'),
          },
          {
            id: DistanceUnit.KILOMETER,
            name: i18n.global.t('options.distanceUnits.kilometers'),
          },
          {
            id: DistanceUnit.FOOT,
            name: i18n.global.t('options.distanceUnits.feet'),
          },
          {
            id: DistanceUnit.MILE,
            name: i18n.global.t('options.distanceUnits.miles'),
          },
        ];
      },
    },
    watch: {
      radiusDistanceInternal() {
        this.triggerChange();
      },
      radiusUnitInternal() {
        this.triggerChange();
      },
      radiusDistanceMin(val) {
        this.radiusDistanceMinInternal = val;
      },
      radiusDistanceMax(val) {
        this.radiusDistanceMaxInternal = val;
      },
      radiusUnit() {
        this.updateRadiusValues();
      },
      radiusInUnit() {
        this.updateRadiusValues();
      },
    },
    mounted() {
      this.updateRadiusValues();
    },
    methods: {
      triggerChange() {
        if (this.preventChangeTrigger) {
          return;
        }

        if (this.triggerChangeTimer !== null) {
          clearTimeout(this.triggerChangeTimer);
          this.triggerChangeTimer = null;
        }

        const radiusInMeters =
          this.radiusDistanceInternal !== this.radiusUnitInternal
            ? UnitConverter.convertDistance(this.radiusDistanceInternal, this.radiusUnitInternal, 'm')
            : this.radiusDistanceInternal;

        this.triggerChangeTimer = setTimeout(() => {
          this.triggerChangeTimer = null;
          this.$emit('radiusChanged', radiusInMeters, this.radiusUnitInternal, this.radiusDistanceInternal);
        }, 50);
      },
      changeRadiusUnit(to) {
        if (this.triggerTriggerChangeTimer !== null) {
          clearTimeout(this.triggerTriggerChangeTimer);
          this.triggerTriggerChangeTimer = null;
        }
        const from = this.radiusUnitInternal;

        this.radiusDistanceForUnitMax = 100000;

        this.preventChangeTrigger = true;
        this.radiusDistanceInternal = Math.max(
          1,
          Math.round(UnitConverter.convertDistance(this.radiusDistanceInternal, from, to)),
        );
        this.radiusDistanceMaxInternal = Math.max(
          1,
          Math.round(UnitConverter.convertDistance(this.radiusDistanceMaxInternal, from, to)),
        );
        this.radiusUnitInternal = to;

        this.updateLimitsAccordingToUnit(to);

        this.triggerTriggerChangeTimer = setTimeout(() => {
          this.triggerTriggerChangeTimer = null;
          this.preventChangeTrigger = false;
          this.triggerChange();
        }, 0);
      },
      updateLimitsAccordingToUnit(unit) {
        switch (unit) {
          case DistanceUnit.METER:
            this.radiusDistanceForUnitMax = 5000;
            break;
          case DistanceUnit.FOOT:
            this.radiusDistanceForUnitMax = 5000;
            break;
          case DistanceUnit.KILOMETER:
            this.radiusDistanceForUnitMax = 100;
            break;
          case DistanceUnit.MILE:
            this.radiusDistanceForUnitMax = 100;
            break;
          default:
            break;
        }
      },
      updateRadiusValues() {
        if (this.radiusUnit) {
          this.radiusUnitInternal = this.radiusUnit;
          this.radiusDistanceInternal = this.radiusInUnit;
        } else if (this.radiusDistanceInMeters < 5000) {
          this.radiusUnitInternal = DistanceUnit.METER;
          this.radiusDistanceInternal = this.radiusDistanceInMeters;
        } else {
          this.radiusUnitInternal = DistanceUnit.KILOMETER;
          this.radiusDistanceInternal = this.radiusDistanceInMeters / 1000;
        }
        this.updateLimitsAccordingToUnit(this.radiusUnitInternal);
      },
    },
  };
</script>

<style lang="scss" rel="stylesheet/scss" scoped>
  #app {
    .radius-distance-control {
      display: flex;
      flex-direction: column;

      &.horizontal {
        flex-direction: row;
        align-items: center;

        .slider {
          margin-left: 1.6rem;
        }
      }

      .text-and-unit {
        display: flex;
        align-items: center;
        margin-bottom: 1rem;

        > svg {
          width: 2.4rem;
          height: 2.4rem;
          margin-right: 0.8rem;
        }

        input {
          width: 7.5rem;
          margin-right: 1.6rem;
        }
      }

      .slider {
        flex-grow: 1;
        margin: 0 1rem;
      }
    }
  }
</style>
